import { ReactNode } from 'react'

import { clsx } from 'clsx'

import { MediaZoom } from './MediaZoom.js'
import { ZoomableItem } from './types.js'

const MAX_VISIBLE_IMAGES = 5

export const MediaGallery = ({
  images,
  className,
}: {
  images: ZoomableItem[]
  className?: string
}) => {
  let imagesPerRow: number[]

  if (images.length === 1) {
    imagesPerRow = [1]
  } else if (images.length === 2) {
    imagesPerRow = [2]
  } else if (images.length === 3) {
    imagesPerRow = [1, 2]
  } else if (images.length === 4) {
    imagesPerRow = [1, 3]
  } else if (images.length >= MAX_VISIBLE_IMAGES) {
    imagesPerRow = [2, 3]
  }

  const hasMore = images.length > MAX_VISIBLE_IMAGES

  const moreThumbnail = hasMore ? images[MAX_VISIBLE_IMAGES] : undefined

  const invisibleImages = images.slice(MAX_VISIBLE_IMAGES)

  const firstRow = images.slice(0, imagesPerRow[0])
  const firstRowMarkup = firstRow.map(image => (
    <MediaZoom.Item
      key={image.id}
      item={image}
      className={firstRow.length === 2 ? 'col-span-3' : 'col-span-6'}
    />
  ))

  const secondRow = images.slice(
    imagesPerRow[0],
    imagesPerRow[0] + imagesPerRow[1],
  )
  const secondRowMarkup = secondRow.map(image => (
    <MediaZoom.Item
      key={image.id}
      item={image}
      className={secondRow.length === 3 ? 'col-span-2' : 'col-span-3'}
    />
  ))

  if (moreThumbnail) {
    const moreThumbnailMarkup = (
      <MediaZoom.Item key={moreThumbnail.id} item={moreThumbnail}>
        {({ open }) => (
          <button
            type="button"
            onClick={open}
            className={secondRow.length === 3 ? 'col-span-2' : 'col-span-3'}
          >
            <MoreIndicator moreCount={images.length - MAX_VISIBLE_IMAGES}>
              <img
                src={moreThumbnail.thumbnail}
                alt={moreThumbnail.alt}
                className="bg-surface-subdued h-full w-full object-cover object-center"
              />
            </MoreIndicator>
          </button>
        )}
      </MediaZoom.Item>
    )

    secondRowMarkup[secondRow.length - 1] = moreThumbnailMarkup
  }

  return (
    <div className={clsx('grid grid-cols-6 gap-1', className)}>
      {firstRowMarkup}
      {secondRowMarkup}
      {invisibleImages.map(image => (
        <MediaZoom.Item key={image.id} item={image} skipRender />
      ))}
    </div>
  )
}

const MoreIndicator = ({
  moreCount,
  children,
}: {
  moreCount: number
  children: ReactNode
}) => {
  return (
    <div className="relative h-full w-full">
      <div
        className={clsx('absolute inset-0 bg-surface-inverse opacity-60')}
      ></div>
      <div
        className={clsx(
          'absolute inset-0 flex items-center justify-center text-content-on-inverse opacity-80 text-lg font-medium leading-none',
        )}
      >
        +{moreCount}
      </div>
      {children}
    </div>
  )
}
